import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { CheckCircleOutline, EventAvailableOutlined, HourglassEmptyOutlined, LayersOutlined } from "@mui/icons-material";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { showToast } from "services/utils/Status";
import { defaultAllSelection } from "services/constants";
import { Loader } from "components/Loader";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { ApproveTable, bulkSelectionCheckboxSx } from "pages/TeamZone/Approve/approveUi";
import { employeeSalaryListHeader } from "pages/PayRoll/PayRollHeaders";
import { GlobalBreadcrumb, sharedStickyFooterBarSx, sharedTableBodyCellSx, sharedTableHeadCellSx } from "components/DesignSystem/sharedUiPatterns";
import {
  APP_BAR_OFFSET,
  StatCard,
  designColors,
  designShell,
  designType,
  designWeights,
  paginationFooter,
  rowsPerPageOptions,
  withDesignFont,
} from "services/constants/designTokens";
import { ToneChip, useFilteredRows, useMeasuredHeight } from "../Shared/salaryUi";
import { ASSIGN_SALARY_PATH, SALARY_REVISION_PATH, formatRupees, toEmployeeRow } from "./employeeSalaryData";
import { compareEmployeeSalary, getEmployeeSalary, getEmployeeSalaryHistory, listEmployeeSalaries } from "./employeeSalaryApi";
import { EmployeeSalaryPanel } from "./EmployeeSalaryPanel";

// Payroll > Salary > Employee Salary: every employee's current salary and
// its history, read-only. Changes go through Assign Salary or Salary
// Revision. On the payroll/salary/ employee-salary APIs (employeeSalaryApi.js).

const ALL = "all";

const STATUS_CARDS = [
  { key: ALL, title: "All", detail: "Employees with a salary", icon: LayersOutlined, accent: designColors.tealDark, tint: designColors.tealTint },
  { key: "Active", title: "Active", detail: "Used in at least one payroll", icon: CheckCircleOutline, accent: "#12B76A", tint: "#E7F8EF" },
  { key: "NotUsed", title: "Active — Not used", detail: "Effective, waiting for first payroll", icon: HourglassEmptyOutlined, accent: "#2878FF", tint: "#EAF2FF" },
  { key: "Upcoming", title: "Upcoming change", detail: "Approved salary starting later", icon: EventAvailableOutlined, accent: "#F0954A", tint: "#FFF4E5" },
];

export const EmployeeSalaryScreen = (props) => {
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  // Fresh state per company.
  return <EmployeeSalaryList key={`${domain}:${topbarCompanyId}`} {...props} />;
};

const EMPTY_PART = { loading: false, data: null, error: "" };

const EmployeeSalaryList = ({ subModuleId }) => {
  const { domain, userScreenAccess, userDetails, topbarCompanyName, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  const validCompany =
    Number.isInteger(Number(topbarCompanyId)) && Number(topbarCompanyId) > 0 && Number(topbarCompanyId) !== defaultAllSelection;
  const history = useHistory();
  const canView = checkEditorPermission(userScreenAccess?.module_details, "Salary Configuration", "Salary Table");
  const canRevise = checkEditorPermission(userScreenAccess?.module_details, "Salary Configuration", "Salary Revision");

  const [employees, setEmployees] = useState([]);
  const [serverCounts, setServerCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(EMPTY_PART);
  const [salaryHistory, setSalaryHistory] = useState(EMPTY_PART);
  const [statusCard, setStatusCard] = useState(ALL);
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [viewId, setViewId] = useState(null);
  const [viewExpanded, setViewExpanded] = useState(true);
  const [filterRef, filterHeight] = useMeasuredHeight();

  const views = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "employee_salary_saved_views",
    fullHeaders: employeeSalaryListHeader,
    screenAccess: canView,
  });

  useEffect(() => {
    if (!validCompany) {
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    listEmployeeSalaries(domain, topbarCompanyId, controller.signal)
      .then((value) => {
        if (controller.signal.aborted) return;
        setEmployees(value.employees);
        setServerCounts(value.counts);
        setLoading(false);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setEmployees([]);
        setLoading(false);
        showToast("error", error.message || "Couldn't load the employee salaries.");
      });
    return () => controller.abort();
  }, [domain, topbarCompanyId, validCompany]);

  const allRows = useMemo(() => employees.map(toEmployeeRow).sort((a, b) => String(a.name).localeCompare(String(b.name))), [employees]);
  const counts = useMemo(
    () => ({
      [ALL]: serverCounts.all ?? allRows.length,
      Active: serverCounts.Active ?? allRows.filter((row) => row.status_key === "Active").length,
      NotUsed: serverCounts.NotUsed ?? allRows.filter((row) => row.status_key === "NotUsed").length,
      Upcoming: serverCounts.Upcoming ?? allRows.filter((row) => row.status_key === "Upcoming").length,
    }),
    [allRows, serverCounts],
  );
  const statusRows = useMemo(() => (statusCard === ALL ? allRows : allRows.filter((row) => row.status_key === statusCard)), [allRows, statusCard]);
  const [visibleRows, handleFilter] = useFilteredRows(statusRows);
  useEffect(() => setPage(0), [statusCard, visibleRows.length]);
  const pageRows = visibleRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const reportMetadata = useMemo(
    () => ({ "Company Name": topbarCompanyName || "N/A", "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss") }),
    [topbarCompanyName],
  );

  const viewEmployee = employees.find((employee) => employee.id === viewId) ?? null;
  useEffect(() => {
    if (!viewEmployee) setViewExpanded(true);
  }, [viewEmployee]);

  // The drawer: Salary Details (single) and Salary History, side by side.
  useEffect(() => {
    if (!viewId) {
      setDetails(EMPTY_PART);
      setSalaryHistory(EMPTY_PART);
      return undefined;
    }
    const controller = new AbortController();
    const { signal } = controller;
    setDetails({ loading: true, data: null, error: "" });
    setSalaryHistory({ loading: true, data: null, error: "" });
    getEmployeeSalary(domain, viewId, signal)
      .then((data) => !signal.aborted && setDetails({ loading: false, data, error: "" }))
      .catch((error) => !signal.aborted && setDetails({ loading: false, data: null, error: error.message || "Couldn't load the salary details." }));
    getEmployeeSalaryHistory(domain, viewId, signal)
      .then((data) => !signal.aborted && setSalaryHistory({ loading: false, data, error: "" }))
      .catch((error) => !signal.aborted && setSalaryHistory({ loading: false, data: null, error: error.message || "Couldn't load the salary history." }));
    return () => controller.abort();
  }, [domain, viewId]);

  const toggle = (id) => setSelected((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));
  const pageAllSelected = pageRows.length > 0 && pageRows.every((row) => selected.includes(row.id));
  const togglePage = () =>
    setSelected((current) => (pageAllSelected ? current.filter((id) => !pageRows.some((row) => row.id === id)) : [...new Set([...current, ...pageRows.map((row) => row.id)])]));

  const headCellSx = { ...sharedTableHeadCellSx, top: APP_BAR_OFFSET + filterHeight, whiteSpace: "nowrap" };
  const bodyCell = { ...sharedTableBodyCellSx, py: 1.25, whiteSpace: "normal" };

  const renderCell = (row, key) => {
    switch (key) {
      case "name":
        return (
          <Mui.Box>
            <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold }}>{row.name}</Mui.Typography>
            <Mui.Typography sx={designType.captionText}>{row.code} · {row.department}</Mui.Typography>
          </Mui.Box>
        );
      case "template":
        return (
          <Mui.Box>
            <Mui.Typography sx={designType.tableBodyCell}>{row.template}</Mui.Typography>
            <Mui.Typography sx={{ ...designType.captionText, ...(row.structure === "Customized" ? { color: "#5B4BD5", fontWeight: designWeights.semibold } : {}) }}>{row.structure}</Mui.Typography>
          </Mui.Box>
        );
      case "gross":
        return (
          <Mui.Box sx={{ textAlign: "right" }}>
            <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold }}>{formatRupees(row.gross)}</Mui.Typography>
            <Mui.Typography sx={designType.captionText}>/ {row.period}</Mui.Typography>
          </Mui.Box>
        );
      case "last_change":
        return (
          <Mui.Box>
            <Mui.Typography sx={designType.tableBodyCell}>{row.last_change}</Mui.Typography>
            <Mui.Typography sx={designType.captionText}>{row.last_change_detail}</Mui.Typography>
          </Mui.Box>
        );
      case "status":
        return (
          <Mui.Box>
            <ToneChip label={row.status} tone={row.status_tone} dot />
            {row.status_detail ? <Mui.Typography sx={{ ...designType.captionText, mt: 0.5 }}>{row.status_detail}</Mui.Typography> : null}
          </Mui.Box>
        );
      default:
        return row[key] || "–";
    }
  };

  return (
    <ThemeProvider theme={withDesignFont}>
      <Mui.Box sx={{ ...designShell.pageSurface, pb: 2 }}>
        <Mui.Box sx={{ ...designShell.headerRowBase, alignItems: { xs: "flex-start", sm: "center" }, px: 2, pt: 2, mb: 2 }}>
          <Mui.Box sx={{ minWidth: 0 }}>
            <GlobalBreadcrumb trail={["Payroll", "Salary", "Employee Salary"]} />
            <Mui.Typography component="h1" sx={designType.pageTitle}>Employee Salary</Mui.Typography>
            <Mui.Typography sx={designType.pageSubtitle}>
              Every employee's current salary and its history. Read-only: changes go through Assign or Revision.
            </Mui.Typography>
          </Mui.Box>
        </Mui.Box>

        <Mui.Box sx={{ px: 2 }}>
          {!validCompany ? (
            <Mui.Typography sx={{ ...designType.helperText, mb: 2 }}>Select a company in the top bar to see its employee salaries.</Mui.Typography>
          ) : null}
          <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" }, gap: 1.5, mb: 2 }}>
            {STATUS_CARDS.map((card) => (
              <StatCard
                key={card.key}
                icon={card.icon}
                title={card.title}
                value={loading ? "–" : counts[card.key]}
                detail={card.detail}
                accent={card.accent}
                tint={card.tint}
                onClick={() => setStatusCard(card.key)}
                isActive={statusCard === card.key}
                activeBorder
              />
            ))}
          </Mui.Box>

          <Mui.Box ref={filterRef} sx={{ position: "sticky", top: APP_BAR_OFFSET, zIndex: 4, backgroundColor: designColors.white }}>
            <CustomFilter
              title="Employee Salary"
              titleOverride={views.titleOverride}
              filterHeaders={views.visibleHeaders}
              data={statusRows}
              filteredData={visibleRows}
              onFilteredDataChange={handleFilter}
              screenAccess={canView}
              hideCount={true}
              downloadFormats={["excel", "pdf", "csv"]}
              includeMetadata={true}
              metadata={reportMetadata}
              labeledToolbar={true}
              searchPlaceholder="Search name or code"
            />
          </Mui.Box>

          {selected.length ? (
            <Mui.Stack direction="row" spacing={1.5} alignItems="center" justifyContent="flex-end" sx={{ ...designShell.sectionCard, borderRadius: 0, mt: 1.5, px: 2, py: 1 }}>
              <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.semibold, color: designColors.slate900 }}>{selected.length} selected</Mui.Typography>
              <Mui.Link component="button" type="button" underline="hover" onClick={() => setSelected([])} sx={{ fontSize: 13, color: designColors.slate600 }}>Clear</Mui.Link>
              {canRevise ? (
                <PrimaryButton onClick={() => history.push(`${SALARY_REVISION_PATH}?revise=${encodeURIComponent(selected.join(","))}`)} sx={{ minWidth: 0, minHeight: 38, px: 2 }}>
                  Revise {selected.length}
                </PrimaryButton>
              ) : null}
            </Mui.Stack>
          ) : null}

          <Mui.Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
            <Mui.TableContainer sx={{ width: "100%", overflowX: "visible" }}>
              <ApproveTable stickyHeader>
                <Mui.TableHead sx={designShell.tableStickyHead}>
                  <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                    <Mui.TableCell padding="checkbox" sx={headCellSx}>
                      <Mui.Checkbox size="small" sx={bulkSelectionCheckboxSx} checked={pageAllSelected} indeterminate={!pageAllSelected && pageRows.some((row) => selected.includes(row.id))} onChange={togglePage} inputProps={{ "aria-label": "Select all employees on this page" }} />
                    </Mui.TableCell>
                    {views.visibleHeaders?.map((head) => (
                      <Mui.TableCell key={head.key} align={head.key === "gross" ? "right" : "left"} sx={headCellSx}>{head.name}</Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {loading ? (
                    <Mui.TableRow>
                      <Mui.TableCell colSpan={(views.visibleHeaders?.length ?? 0) + 1} sx={{ borderBottom: "none" }}>
                        <Loader info="Loading..." minHeight="40vh" />
                      </Mui.TableCell>
                    </Mui.TableRow>
                  ) : visibleRows.length === 0 ? (
                    <TableEmptyState dataLength={statusRows} colSpan={(views.visibleHeaders?.length ?? 0) + 1} />
                  ) : (
                    pageRows.map((row) => (
                      <Mui.TableRow key={row.id} hover onClick={() => setViewId(row.id)} sx={{ cursor: "pointer", ...designShell.tableRowHover, "&:last-child td": { borderBottom: "none" } }}>
                        <Mui.TableCell padding="checkbox" sx={bodyCell} onClick={(event) => event.stopPropagation()}>
                          <Mui.Checkbox size="small" sx={bulkSelectionCheckboxSx} checked={selected.includes(row.id)} onChange={() => toggle(row.id)} inputProps={{ "aria-label": `Select ${row.name}` }} />
                        </Mui.TableCell>
                        {views.visibleHeaders.map((head) => (
                          <Mui.TableCell key={head.key} align={head.key === "gross" ? "right" : "left"} sx={bodyCell}>{renderCell(row, head.key)}</Mui.TableCell>
                        ))}
                      </Mui.TableRow>
                    ))
                  )}
                </Mui.TableBody>
              </ApproveTable>
            </Mui.TableContainer>
            <Mui.Box sx={sharedStickyFooterBarSx}>
              <Mui.Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }} justifyContent="space-between">
                <Mui.Box sx={{ flex: 1, minWidth: 0 }}>
                  {paginationFooter({
                    page,
                    rowsPerPage,
                    total: visibleRows.length,
                    itemLabel: "employees",
                    onPageChange: setPage,
                    onRowsPerPageChange: (event) => {
                      setRowsPerPage(parseInt(event.target.value, 10));
                      setPage(0);
                    },
                    rowsPerPageOptions,
                  })}
                </Mui.Box>
                <Mui.Typography sx={{ ...designType.captionText, flexShrink: 0 }}>
                  Not configured employees are in{" "}
                  <Mui.Link component="button" type="button" underline="hover" onClick={() => history.push(ASSIGN_SALARY_PATH)} sx={{ fontSize: 12, fontWeight: designWeights.semibold, color: designColors.tealDark }}>Assign Salary</Mui.Link>
                </Mui.Typography>
              </Mui.Stack>
            </Mui.Box>
          </Mui.Box>
        </Mui.Box>
      </Mui.Box>

      <EmployeeSalaryPanel
        open={Boolean(viewEmployee)}
        employee={viewEmployee}
        details={details}
        history={salaryHistory}
        canRevise={canRevise}
        onCompare={(a, b) => compareEmployeeSalary(domain, viewId, a, b)}
        expanded={Boolean(viewEmployee) && viewExpanded}
        onToggleExpand={() => setViewExpanded((value) => !value)}
        onClose={() => setViewId(null)}
      />
    </ThemeProvider>
  );
};
